import React, { useState } from 'react';
import { View, TouchableOpacity } from 'react-native';
import IconInput from '../../../components/IconInput/IconInput';
import MapModal from '../../../components/MapModal/MapModal';
import InputField from '../../../components/input/input';
import Typography from '../../../components/Text/Text';
import Button from '../../../components/Button/Button';
import Divider from '../../../components/Divider/Divider';
import map from 'src/assets/map.png';
import SwitchSection from './SwitchSection';
import { Address, initialMapAddress } from 'src/types/common/address';
import { formatAddressToString } from 'src/helpers/addressHelper';
import { styles } from '../screens/Policy/PolicyScreen.styles';

export interface AdditionalInsuredEntry {
  name: string;
  address: Address;
}

interface AdditionalInsuredSectionProps {
  additionalInsured: {
    value: 'yes' | 'no' | null;
    set: (value: 'yes' | 'no') => void;
    entries: AdditionalInsuredEntry[];
    setEntries: (updater: (prev: AdditionalInsuredEntry[]) => AdditionalInsuredEntry[]) => void;
  };
}

const AdditionalInsuredSection: React.FC<AdditionalInsuredSectionProps> = ({
  additionalInsured,
}) => {
  const [activeMapIndex, setActiveMapIndex] = useState<number | null>(null);

  const handleToggleChange = (newValue: 'yes' | 'no') => {
    additionalInsured.set(newValue);
    if (newValue === 'yes' && additionalInsured.entries.length === 0) {
      additionalInsured.setEntries(() => [{ name: '', address: initialMapAddress }]);
    }
  };

  const handleNameChange = (index: number, name: string) => {
    additionalInsured.setEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, name } : entry)),
    );
  };

  const handleAddressChange = (index: number, address: Address) => {
    additionalInsured.setEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, address } : entry)),
    );
    setActiveMapIndex(null);
  };

  const handleAddEntry = () => {
    additionalInsured.setEntries((prev) => [...prev, { name: '', address: initialMapAddress }]);
  };

  const handleRemoveEntry = (index: number) => {
    additionalInsured.setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <SwitchSection<'yes' | 'no'>
      label="Do you want to add an additional insured?"
      parentTestID={'policy-final-addition-insured'}
      value={additionalInsured.value}
      onChange={handleToggleChange}
      buttons={[
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
      ]}
      childrenRenderCondition={(selectedValue) => selectedValue === 'yes'}>
      {additionalInsured.entries.map((entry, index) => (
        <View key={index} style={styles.entryContainer}>
          {index > 0 && <Divider style={styles.entrySeparator} />}
          {additionalInsured.entries.length > 1 && (
            <View style={styles.entryHeader}>
              <Typography variant="title3" style={styles.entryHeaderText}>
                Additional Insured #{index + 1}
              </Typography>
              <TouchableOpacity onPress={() => handleRemoveEntry(index)}>
                <Typography variant="body2" style={styles.removeText}>
                  Remove
                </Typography>
              </TouchableOpacity>
            </View>
          )}
          <InputField
            variant="caption"
            label="Full name of the additional insured"
            placeholder="Please insert the full name of the additional insured"
            value={entry.name}
            onChange={(text) => handleNameChange(index, text)}
          />
          <IconInput
            label="Address of the additional insured"
            value={
              entry.address.street && entry.address.street.length > 0
                ? formatAddressToString(entry.address)
                : ''
            }
            placeholder="Please insert the address of the additional insured"
            iconSource={map}
            iconWidth={20}
            iconHeight={20}
            onPress={() => setActiveMapIndex(index)}
            onIconPress={() => setActiveMapIndex(index)}
          />
          <MapModal
            isVisible={activeMapIndex === index}
            onClose={() => setActiveMapIndex(null)}
            onSave={(newAddress) => handleAddressChange(index, newAddress)}
            address={entry.address}
            initialPosition={{
              lat: entry.address.latitude,
              lng: entry.address.longitude,
            }}
            title="Add Additional Insured Address"
          />
        </View>
      ))}
      <Button
        testID="add-another-insured"
        type="tertiary"
        title="+ Add Another Insured"
        onPress={handleAddEntry}
      />
    </SwitchSection>
  );
};

export default AdditionalInsuredSection;
